import { useLayoutEffect, useRef } from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import {
    computeLrShortcodePopupPosition,
    formatShortcodeCode,
    LR_SHORTCODE_POPUP_WIDTH,
    lrShortcodeTriggerTestId,
} from "../configTabHelpers";

import type { LrShortcodePortalProps } from "../../../types";

export function LrShortcodePortal({ tooltip, onClose, onPositionChange }: LrShortcodePortalProps) {
    const panelRef = useRef<HTMLDivElement>(null);

    // Re-anchor using the real panel height so "above" sits tight to the icon (est. height was too tall).
    useLayoutEffect(() => {
        if (!tooltip || !onPositionChange) return;
        const panel = panelRef.current;
        if (!panel) return;
        const trigger = document.querySelector<HTMLElement>(
            `[data-testid="${lrShortcodeTriggerTestId(tooltip.idx)}"]`,
        );
        if (!trigger) return;
        const measuredHeight = panel.getBoundingClientRect().height;
        if (measuredHeight <= 0) return;
        const next = computeLrShortcodePopupPosition(
            trigger.getBoundingClientRect(),
            window,
            measuredHeight,
        );
        if (
            next.top === tooltip.top &&
            next.left === tooltip.left &&
            next.placement === tooltip.placement
        ) {
            return;
        }
        onPositionChange(next);
    }, [tooltip, onPositionChange]);

    if (!tooltip || typeof document === "undefined") return null;
    return createPortal(
        <div
            ref={panelRef}
            data-testid="company-config-lr-shortcode-popup"
            data-lr-shortcode-popup
            className="fixed z-[10050] max-h-[80vh] overflow-x-hidden overflow-y-auto rounded-xl border border-amber-200 bg-amber-50 px-3 pb-3 pt-6 text-amber-900 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.45)] select-text"
            style={{
                top: tooltip.top,
                left: tooltip.left,
                width: LR_SHORTCODE_POPUP_WIDTH,
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Shortcodes"
        >
            {tooltip.placement === "right" ? (
                <div
                    className="pointer-events-none absolute right-full top-7 h-0 w-0 border-y-[7px] border-y-transparent border-r-[8px] border-r-[#fffbeb]"
                    aria-hidden
                />
            ) : (
                <div
                    className="pointer-events-none absolute left-full top-7 h-0 w-0 border-y-[7px] border-y-transparent border-l-[8px] border-l-[#fffbeb]"
                    aria-hidden
                />
            )}
            <button
                type="button"
                data-testid="company-config-lr-shortcode-popup-close-button"
                className="absolute right-2 top-2 rounded p-1 text-amber-800 transition-colors hover:bg-amber-100 hover:text-amber-950"
                onClick={onClose}
                aria-label="Close shortcodes"
            >
                <X className="h-4 w-4" strokeWidth={2} />
            </button>
            {tooltip.codes && tooltip.codes.length > 0 ? (
                <div className="mx-auto w-full text-center break-words">
                    <h3 className="mb-2 text-[14px] font-bold tracking-tight text-amber-950">Shortcodes</h3>
                    <div className="space-y-1.5 text-[13px] leading-snug text-amber-900">
                        {tooltip.codes.map((sc, i) => (
                            <p key={i} className="m-0">
                                {sc.label ?? sc.name} -{" "}
                                <strong className="font-bold">
                                    {formatShortcodeCode(sc.code ?? sc.variable)}
                                </strong>
                            </p>
                        ))}
                    </div>
                </div>
            ) : (
                <p className="m-0 text-center text-[13px] text-amber-900/95">
                    No shortcodes are available!
                </p>
            )}
        </div>,
        document.body,
    );
}
